import type {
  AdPlacementRow,
  BannerRow,
  EconomyRow,
  ExperimentRow,
  MonetizationFreshness,
  MonetizationQuery,
  MonetizationResult,
  MonetizationSummary,
  MonetizationTab,
  OfferRow,
  SegmentRow,
  SkuRow,
} from '@/types';

/** The tabs on the page. "Banners & Offers" shows two backend tabs. */
export type PageTab =
  | 'summary'
  | 'skus'
  | 'funnels'
  | 'ads'
  | 'economy'
  | 'segments'
  | 'experiments';

export const PAGE_TABS: Array<{ key: PageTab; label: string }> = [
  { key: 'summary', label: 'Summary' },
  { key: 'skus', label: 'SKUs' },
  { key: 'funnels', label: 'Banners & Offers' },
  { key: 'ads', label: 'Ads' },
  { key: 'economy', label: 'Economy' },
  { key: 'segments', label: 'Segments' },
  { key: 'experiments', label: 'Experiments' },
];

/** The backend calls a page tab makes when first opened. */
export const TAB_CALLS: Record<PageTab, MonetizationTab[]> = {
  summary: ['summary'],
  skus: ['skus'],
  funnels: ['banners', 'offers'],
  ads: ['ads'],
  economy: ['economy'],
  segments: ['segments'],
  experiments: ['experiments'],
};

/** The page tabs a country filter applies to (the offer cards have no country). */
export const COUNTRY_TABS: PageTab[] = ['skus', 'funnels', 'ads', 'segments'];

export const BACKEND_TABS: MonetizationTab[] = [
  'summary',
  'skus',
  'banners',
  'offers',
  'ads',
  'economy',
  'segments',
  'experiments',
];

/** `data` of each backend tab. */
export interface TabData {
  summary: MonetizationSummary;
  skus: SkuRow[];
  banners: BannerRow[];
  offers: OfferRow[];
  ads: AdPlacementRow[];
  economy: EconomyRow[];
  segments: SegmentRow[];
  experiments: ExperimentRow[];
}

/** What the controls hold. `to` is excluded. */
export interface MonetizationFilters {
  from: string;
  to: string;
  /** skus, banners, ads and segments only. */
  country?: string;
  /** experiments only. */
  experimentKey?: string;
}

/** The query a backend tab is asked with: only the filters that tab uses. */
export function queryFor(tab: MonetizationTab, f: MonetizationFilters): MonetizationQuery {
  const query: MonetizationQuery = { from: f.from, to: f.to };
  const usesCountry = tab === 'skus' || tab === 'banners' || tab === 'ads' || tab === 'segments';
  if (usesCountry && f.country) query.country = f.country;
  if (tab === 'experiments' && f.experimentKey) query.experimentKey = f.experimentKey;
  return query;
}

/**
 * What a cached answer depends on. Experiments ignore the range (the backend reads the last 90
 * days of assignments), so their key leaves `from` and `to` out: a range change keeps them.
 */
const keyFor = (tab: MonetizationTab, f: MonetizationFilters): string => {
  const { from, to, ...rest } = queryFor(tab, f);
  return JSON.stringify(tab === 'experiments' ? rest : { from, to, ...rest });
};

export type TabEntry =
  | { status: 'idle' }
  | { status: 'loading'; key: string; token: number }
  | { status: 'loaded'; key: string; result: MonetizationResult<unknown> }
  | { status: 'failed'; key: string; message: string };

export interface MonetizationState {
  filters: MonetizationFilters;
  entries: Record<MonetizationTab, TabEntry>;
  /**
   * Freshness of the last summary that had data. It describes the build, not the range, so it
   * survives a range change and the stale banner does not blink off while the summary reloads.
   */
  freshness: MonetizationFreshness | null;
}

export type MonetizationAction =
  /** The controls changed: every tab whose query changed is cleared. */
  | { type: 'filters'; filters: MonetizationFilters }
  /** A backend call is going out; `token` identifies it. */
  | { type: 'start'; tab: MonetizationTab; token: number }
  | { type: 'loaded'; tab: MonetizationTab; token: number; result: MonetizationResult<unknown> }
  | { type: 'failed'; tab: MonetizationTab; token: number; message: string };

export function initialMonetizationState(filters: MonetizationFilters): MonetizationState {
  const entries = {} as Record<MonetizationTab, TabEntry>;
  for (const tab of BACKEND_TABS) entries[tab] = { status: 'idle' };
  return { filters, entries, freshness: null };
}

const setEntry = (
  state: MonetizationState,
  tab: MonetizationTab,
  entry: TabEntry
): MonetizationState => ({ ...state, entries: { ...state.entries, [tab]: entry } });

/**
 * Rules:
 * - a tab is fetched when first opened, never again while its query is unchanged;
 * - a change of the controls clears the tabs whose answer depends on what changed (a new range
 *   clears all of them but Experiments, which ignores the range), including one still loading,
 *   whose answer is then dropped;
 * - an answer counts only for the call (token) that is still loading, so a stale one is dropped.
 */
export function monetizationReducer(
  state: MonetizationState,
  action: MonetizationAction
): MonetizationState {
  switch (action.type) {
    case 'filters': {
      const entries = { ...state.entries };
      for (const tab of BACKEND_TABS) {
        const entry = entries[tab];
        if (entry.status !== 'idle' && entry.key !== keyFor(tab, action.filters)) {
          entries[tab] = { status: 'idle' };
        }
      }
      return { ...state, filters: action.filters, entries };
    }
    case 'start': {
      const entry = state.entries[action.tab];
      if (entry.status === 'loading' || entry.status === 'loaded') return state;
      return setEntry(state, action.tab, {
        status: 'loading',
        key: keyFor(action.tab, state.filters),
        token: action.token,
      });
    }
    case 'loaded': {
      const entry = state.entries[action.tab];
      if (entry.status !== 'loading' || entry.token !== action.token) return state;
      const next = setEntry(state, action.tab, {
        status: 'loaded',
        key: entry.key,
        result: action.result,
      });
      if (action.tab === 'summary') {
        const summary = action.result.data as MonetizationSummary | null;
        if (summary?.freshness) return { ...next, freshness: summary.freshness };
      }
      return next;
    }
    case 'failed': {
      const entry = state.entries[action.tab];
      if (entry.status !== 'loading' || entry.token !== action.token) return state;
      return setEntry(state, action.tab, {
        status: 'failed',
        key: entry.key,
        message: action.message,
      });
    }
  }
}

/** The backend calls to make now for a page tab: those not loading, not loaded. */
export function callsToMake(state: MonetizationState, tab: PageTab): MonetizationTab[] {
  return TAB_CALLS[tab].filter((t) => {
    const s = state.entries[t].status;
    return s === 'idle' || s === 'failed';
  });
}

/** The entry of a backend tab with its `data` typed. */
export type TypedEntry<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; result: MonetizationResult<T> }
  | { status: 'failed'; message: string };

export function entryOf<K extends MonetizationTab>(
  state: MonetizationState,
  tab: K
): TypedEntry<TabData[K]> {
  const e = state.entries[tab];
  if (e.status === 'loaded') {
    return { status: 'loaded', result: e.result as MonetizationResult<TabData[K]> };
  }
  if (e.status === 'failed') return { status: 'failed', message: e.message };
  return { status: e.status };
}
